import React, { useState, useEffect } from "react";
import { Button, ButtonGroup, Col, Input, InputGroup, Row } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import DeleteModal from "../../Components/Common/DeleteModal";
import SupplierAddEdit from "./Components/SupplierAddEdit";
import SupplierServices from "../../Services/QRCodeServices/SupplierServices/SupplierServices";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const SupplierList = () => {
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const handleFilter = (event) => {
    setFilterText(event.target.value);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedSupplier(null);
  };

  const toggleDelete = () => {
    setIsOpenDelete(!isOpenDelete);
  };

  const handleEditClick = (supplier) => {
    setSelectedSupplier(supplier);
    setModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedSupplier(null);
    setModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await SupplierServices.delete(selectedDeleteItem.id);
        fetchSuppliers();
        toast.success("Supplier deleted successfully");
      } catch (error) {
        toast.error("Failed to delete supplier");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleSuccess = () => {
    fetchSuppliers();
  };

  const columns = [
    {
      name: "S.N",
      selector: (row) => row.index,
      sortable: true,
      width: "75px",
    },
    {
      name: "Supplier Name",
      selector: (row) => row.supplierName,
      sortable: true,
    },
    {
      name: "Location",
      selector: (row) => row.location || "N/A",
      sortable: true,
      wrap: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <ButtonGroup size="sm">
          <Button
            color="btn btn-soft-warning"
            className="btn-sm gap-1 ms-1"
            size="sm"
            onClick={() => handleEditClick(row)}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            className="btn btn-soft-danger btn-sm gap-1 ms-1"
            color="danger"
            onClick={() => handleDeleteClick(row)}
            size="sm"
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </ButtonGroup>
      ),
      center: true,
    },
  ];

  const data = supplierList
    ?.filter((item) => {
      const searchText = filterText.toLowerCase();
      return item.supplierName.toLowerCase().includes(searchText);
    })
    .map((item, index) => ({
      ...item,
      index: index + 1,
    }));

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await SupplierServices.getList();
      setSupplierList(response.data);
    } catch (error) {
      toast.error(error?.response?.data || "Failed to fetch suppliers");
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Supplier Settings" pageTitle="Suppliers" />
      </div>

      <Row className="mb-2">
        <Col lg={5} className="mb-2">
          <InputGroup>
            <Input
              placeholder="Search by supplier name"
              value={filterText}
              onChange={handleFilter}
            />
          </InputGroup>
        </Col>
        <Col lg={7}>
          <div className="d-flex justify-content-end">
            <Button className="btn btn-success" onClick={handleCreateClick}>
              + Create Supplier
            </Button>
          </div>
        </Col>
      </Row>

      <CustomDataTable
        responsive
        striped
        pagination
        fixedHeader
        persistTableHead
        progressPending={loading}
        customStyles={customStyles}
        progressComponent={
          <div className="my-3">
            <Triangle
              visible={true}
              height="80"
              width="80"
              color="#5B71B9"
              ariaLabel="triangle-loading"
            />
            <h5 className="mt-1">Loading...</h5>
          </div>
        }
        columns={columns}
        data={data}
      />

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />

      <SupplierAddEdit
        isOpen={modalOpen}
        toggle={toggleModal}
        supplierData={selectedSupplier}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default SupplierList;
